import React, {useEffect, useState} from 'react'
import ImageCarousel from '../components/Carousel'

import Check from '../../../../media/images/check.svg'
import CheckBlue from '../../../../media/images/check-blue.svg'
import word from '../../../../media/images/word.svg'
import powerpoint from '../../../../media/images/powerpoint.svg'
import excel from '../../../../media/images/excel.svg'
import indesign from '../../../../media/images/indesign.svg'
import figma from '../../../../media/images/figma.svg'
import ai from '../../../../media/images/ai.svg'
import dw from '../../../../media/images/dw.svg'
import pdf from '../../../../media/images/pdf.svg'
import Share from '../../../../media/images/share.svg'
import ShareModal from '../../widgets/components/ShareModal'
import {downloadResourceApi, getDownloadedResourcesData} from '../core/_requests'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import {useAuth} from '../../../../app/modules/auth'
// import {useNavigate} from 'react-router-dom'
import {useThemeMode} from '../../../../_metronic/partials'
import Skeleton from 'react-loading-skeleton'
import {useDateFormat} from '../../../hooks/useDateFormat'
import {DateTime} from 'luxon'
import {toTitleCase, sanitizeFileName} from '../../../core/_util'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'

interface ResourcesDetailsProps {
  resourceData: any // Using any type for data
  loading: boolean
}

const ResourcesDetails: React.FC<ResourcesDetailsProps> = ({resourceData, loading}) => {
  const {mode} = useThemeMode()
  const {formatMessage} = useIntl()
  const charLimit = 100 // Adjust the limit as needed
  const {companyId, currentUser} = useAuth()
  // const navigate = useNavigate()
  const {getDateValue} = useDateFormat()
  const [AllDownloadedResourcesData, setAllDownloadedResourcesData] = useState<any[]>([])

  // Splitting the text based on the character limit
  const firstPart = resourceData?.description?.substr(0, charLimit)
  const secondPart = resourceData?.description?.substr(charLimit)

  // State to manage whether the extra text is visible
  const [isExpanded, setIsExpanded] = useState(false)

  const [isDownloaded, setIsDownloaded] = useState<boolean>(false)

  // Function to toggle the expanded state
  const toggleText = () => {
    setIsExpanded(!isExpanded)
  }

  const formatImages: {[key: string]: string} = {
    doc: word,
    figma: figma,
    ppt: powerpoint,
    dw: dw,
    ai: ai,
    indd: indesign,
    xls: excel,
    pdf: pdf,
  }

  const [downloadButtonText, setDownloadButtonText] = useState(formatMessage({id: 'Download Now'}))
  const [isDownloading, setIsDownloading] = useState(false)
  const downloadResource = async () => {
    try {
      setIsDownloading(true)
      setDownloadButtonText(formatMessage({id: 'Downloading...'}))

      const payload = {
        resourceId: resourceData?.resourceId,
        versionId: resourceData?.latestVersionId,
      }

      const {
        data: {success, data, errors},
      } = await downloadResourceApi(payload, companyId!)

      if (success) {
        if (data.zipUrl) {
          const zipFileName = `${sanitizeFileName(toTitleCase(resourceData?.name))}-v${
            resourceData?.latestVersion?.version
          }.zip`
          // Download file
          const response = await fetch(data.zipUrl)
          const blob = await response.blob()

          // Create a download link
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', zipFileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.ResourcesDownloaded,
              action: 'normal',
              metadata: {
                company_id: companyId,
                resource_id: resourceData?.resourceId,
                version_id: resourceData?.latestVersionId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
              },
            },
          })
        }
      } else {
        errors?.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ResourcesDownloadFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              resource_id: resourceData?.resourceId,
              version_id: resourceData?.latestVersionId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              error: errors[0],
            },
          },
        })
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.ResourcesDownloadFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            resource_id: resourceData?.resourceId,
            version_id: resourceData?.latestVersionId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
          error: err,
          },
        },
      })
    } finally {
      setIsDownloading(false)
      setDownloadButtonText(formatMessage({id: 'Download Now'}))
    }
  }

  const [isActive, setIsActive] = useState(false)
  const [urlToShare, setUrlToShare] = useState('')
  const handleModal = (url: any) => {
    setIsActive(() => !isActive)
    setUrlToShare(url)
    setIsActive(true)
  }

  const [lastUpdated, setLastUpdated] = useState('')

  useEffect(() => {
    if (resourceData && resourceData.lastUpdatedOn) {
      setLastUpdated(resourceData.lastUpdatedOn.slice(0, 10))
    }
  }, [resourceData])

  const getAllDownloadedResources = async () => {
    try {
      const {
        data: {success, data, errors},
      } = await getDownloadedResourcesData(companyId!)
      if (success) {
        // Handle downloaded resources data if needed
        setAllDownloadedResourcesData(data)
      } else {
        errors?.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      console.error('Error fetching downloaded resources:', err)
      toast.error(formatMessage({id: 'Failed to fetch downloaded resources'}))
    }
  }

  useEffect(() => {
    getAllDownloadedResources()
  }, []) // eslint-disable-line

  const isResourceDownloaded = (resourceId: string) => {
    return AllDownloadedResourcesData.some(
      (downloadedResource) => downloadedResource.resource.resourceId === resourceId
    )
  }

  useEffect(() => {
    if (AllDownloadedResourcesData.length > 0 && resourceData.resourceId) {
      setIsDownloaded(isResourceDownloaded(resourceData.resourceId))
    }
  }, [AllDownloadedResourcesData, resourceData]) // eslint-disable-line

  return (
    <>
      {loading ? (
        <div className='row'>
          <div className='col-md-6 h-fc'>
            <Skeleton
              height={300}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              width={140}
              height={80}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          </div>
          <div className='resourcesDetails col-md-6 h-fc'>
            <div className='d-flex justify-content-between'>
              <div>
                <Skeleton
                  height={30}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={30}
                  width={30}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
            <Skeleton
              height={40}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={20}
              width={200}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={30}
              width={200}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={60}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <div className='d-flex gap-2'>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div>
                <Skeleton
                  height={20}
                  width={60}
                  className='my-1'
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
            <Skeleton
              height={50}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
            <Skeleton
              height={30}
              count={6}
              width={400}
              className='my-1'
              baseColor='var(--kt-skeleton-bg-color)'
              highlightColor='var(--kt-skeleton-highlight-bg-color)'
            />
          </div>
        </div>
      ) : (
        <div className='row'>
          <div className='col-md-6 h-fc'>
            <ImageCarousel images={resourceData?.previewImage} />
          </div>

          <div className='resourcesDetails col-md-6 h-fc'>
            <div className='d-flex justify-content-between'>
              <div className={`px-4 py-2 category` + (mode === `dark` ? `-dark` : ``)}>
                {resourceData?.category}
              </div>
              <div className='d-flex align-items-center'>
                <button
                  className='btn m-0 p-0 d-block'
                  onClick={() => handleModal(window.location.href)}
                >
                  <img
                    src={Share}
                    alt='share'
                    className='share align-self-center'
                    style={{height: '24px', width: '24px', marginRight: '14px'}}
                  />
                </button>
              </div>
            </div>
            {isActive && (
              <ShareModal isActive={isActive} setIsActive={setIsActive} url={urlToShare} />
            )}
            <div className='ResourcesTitle d-flex mt-2'>
              <h1>{resourceData?.name}</h1>
              <p className='resourceVersion h-100'>v{resourceData?.latestVersion?.version}</p>
            </div>

            <div>
              <p
                className='px-3 py-2 m-0 font-size-12'
                style={{
                  display: 'inline-block',
                  borderRadius: '4px',
                  background: 'rgba(71, 118, 230, 0.10)',
                  color: '#4776E6',
                }}
              >
                {formatMessage({id: 'Last Updated:'})}
                <span>
                  &nbsp;
                  {getDateValue(lastUpdated?.toString())
                    ? getDateValue(lastUpdated?.toString())
                    : DateTime.fromISO(lastUpdated?.toString()).toLocaleString(DateTime.DATE_MED)}
                </span>
              </p>
            </div>

            <div className='mt-4 d-flex align-items-center gap-2'>
              <button
                className='btn btn-primary'
                onClick={downloadResource}
                disabled={isDownloading}
              >
                {downloadButtonText}
              </button>
              {isDownloaded && (
                <img src={Check} alt='check' title={formatMessage({id: 'Already Downloaded'})} />
              )}
            </div>

            <div className='resourcesFormat d-flex my-2'>
              <p>{formatMessage({id: 'Available Formats:'})}</p>
              <div className='formatImages'>
                {resourceData?.latestVersion?.fileFormats?.map((fileFormat: any, index: any) => {
                  const imageSrc = formatImages[fileFormat.format]
                  return imageSrc ? (
                    <img
                      key={index}
                      src={imageSrc}
                      alt={fileFormat.format}
                      className='selected'
                      style={{
                        width: '32px',
                        height: '32px',
                        cursor: 'pointer',
                        opacity: 1,
                      }}
                    />
                  ) : null
                })}
              </div>
            </div>

            <div className='resourcesTags my-4'>
              {resourceData?.tags?.map((tag: any, index: any) => {
                return (
                  <p key={index} className='tag'>
                    #{tag.tagName}
                  </p>
                )
              })}
            </div>

            <div className='resourceDesc'>
              <p>
                {firstPart}
                {secondPart && isExpanded && <span>{secondPart}</span>}&nbsp;
                {secondPart && (
                  <button className='readmore' onClick={toggleText}>
                    {isExpanded
                      ? formatMessage({id: 'Show Less'})
                      : formatMessage({id: 'Read More'})}
                  </button>
                )}
              </p>
            </div>

            <div className='resourceBenefits'>
              <h3>{formatMessage({id: 'Benefits'})}</h3>
              <div className='benefitsList'>
                {resourceData?.benefits?.map((val: any, index: any) => {
                  return (
                    <div className='benefit' key={index}>
                      <img src={CheckBlue} alt='' />
                      <p>{val}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ResourcesDetails
